import { Download, FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Variants } from '@/types/common';
import { showAlert } from '../../../../slice/AlertSlice';
import { downloadBlob } from '../../../../util/downloadFile';
import { is4xxError } from '../../../../services/api-base';
import { useDownloadInvoicePdfMutation } from '../../../orders/services/orderApi';

interface OrderAmountCellProps {
  amount: string;
  orderNumber: string | null;
  // DV-2357: set by getOrders after an S3 existence check. The invoice controls
  // render only when this is true, so they never appear for orders that have no
  // fetchable invoice. Absent on an older backend → false → controls hidden.
  invoiceAvailable: boolean;
}

// DV-2357: the "Amount" column cell. Shows the order amount and, when the order
// has a fetchable invoice (invoiceAvailable), compact "View" / "Download"
// controls that read the NetSuite-published invoice PDF via order-service. View
// opens the PDF inline in a new tab; Download saves it. A 404 (a rare race — the
// invoice was removed after the list loaded) surfaces a "not available" alert
// rather than an error page.
export function OrderAmountCell({
  amount,
  orderNumber,
  invoiceAvailable
}: OrderAmountCellProps) {
  const dispatch = useDispatch();
  const [downloadInvoicePdf] = useDownloadInvoicePdfMutation();
  // Which control is fetching — drives a spinner on that control and disables
  // both while in flight (feedback for the async read + decode, and a guard so a
  // double-click can't fire two S3 reads).
  const [busy, setBusy] = useState<'view' | 'download' | null>(null);

  const fetchInvoice = async (download: boolean) => {
    if (!orderNumber || busy) return;
    setBusy(download ? 'download' : 'view');
    // Open the target tab synchronously (before the await) so it isn't caught by
    // the popup blocker; close it again if the fetch fails.
    const viewTab = download ? null : window.open('', '_blank');
    if (viewTab) {
      // Interim page so the tab is never a blank white screen while the PDF
      // fetches; on failure it's closed below.
      viewTab.document.write(
        '<!doctype html><title>Loading invoice…</title>' +
          '<body style="margin:0;padding:24px;font-family:sans-serif;color:#555">Loading invoice…</body>'
      );
    }
    const fileName = `order_${orderNumber}_invoice.pdf`;
    try {
      const blob = await downloadInvoicePdf({ orderNumber, download }).unwrap();
      if (download) {
        downloadBlob(blob, fileName);
      } else if (viewTab) {
        const objectUrl = window.URL.createObjectURL(blob);
        viewTab.location.href = objectUrl;
        // Revoke after the new tab has had time to load the document.
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
      } else {
        // Popup blocked → fall back to a download (downloadBlob manages its own object URL).
        downloadBlob(blob, fileName);
      }
    } catch (error) {
      viewTab?.close();
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: is4xxError(error)
            ? 'No invoice is available for this order yet.'
            : 'Could not load the invoice. Please try again.'
        })
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="font-semibold text-foreground">{amount}</div>
      {invoiceAvailable && (
        <TooltipProvider delayDuration={200}>
          <div className="mt-2 flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => void fetchInvoice(false)}
                  disabled={busy !== null}
                  aria-label="View Invoice"
                  className="inline-flex h-7 items-center gap-1.5 rounded border border-primary bg-background px-2.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {busy === 'view' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )}
                  View
                </button>
              </TooltipTrigger>
              <TooltipContent>View Invoice</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => void fetchInvoice(true)}
                  disabled={busy !== null}
                  aria-label="Download Invoice"
                  className="inline-flex h-7 w-7 items-center justify-center rounded border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {busy === 'download' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>Download Invoice</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}
